import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useContext, useMemo } from 'react';

import { Color } from '@/constants/color';
import { RewardCardStatus } from '@/constants/rewardCard';
import AppContext from '@/contexts/appContext';
import DisabledTooltip from '@/pages/RewardCardList/components/DisabledTooltip';
import RewardCardRepository from '@/repositories/RewardCardRepository';
import Button from '@/ui/components/Button';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import { Props } from './types';

const StatusUpdateButton = ({ account, hasCurrentActiveCard, disabled = false }: Props) => {
  const { addMessage } = useContext(AppContext);
  const rewardCardRepository = RewardCardRepository();
  const { account_id, current_card } = account;
  const isActive = current_card?.card_status === RewardCardStatus.ACTIVE;
  const isBlocked = current_card?.card_status === RewardCardStatus.BLOCKED;

  const { isPending, mutate } = useMutation({
    mutationFn: rewardCardRepository.setStatus,
    onSuccess: () => {
      addMessage('success', 'Account status successfully updated');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const updateStatus = useCallback(() => {
    const status = isActive ? RewardCardStatus.BLOCKED : RewardCardStatus.ACTIVE;

    return mutate({ accountId: account_id, status });
  }, [account_id, isActive, mutate]);

  const confirmAndUpdateStatus = useCallback(() => {
    return confirm(
      'Are you sure you want to unblock? Unblocking this card will block the currently active card'
    )
      ? updateStatus()
      : null;
  }, [updateStatus]);

  const handleClick = useCallback(() => {
    return isBlocked && hasCurrentActiveCard ? confirmAndUpdateStatus() : updateStatus();
  }, [confirmAndUpdateStatus, hasCurrentActiveCard, isBlocked, updateStatus]);

  const renderButton = useMemo(() => {
    return (
      <Button
        color={isActive ? Color.ORANGE : Color.BLUE}
        disabled={isPending || disabled}
        onClick={handleClick}
        size="xs">
        {isPending ? 'Updating...' : isActive ? 'Block' : isBlocked ? 'Unblock' : ''}
      </Button>
    );
  }, [disabled, handleClick, isActive, isBlocked, isPending]);

  if (isActive || isBlocked) {
    return disabled ? <DisabledTooltip>{renderButton}</DisabledTooltip> : renderButton;
  }

  return null;
};

export default StatusUpdateButton;
